/**
 * Simple daily interest accrual — "%/năm" applied per calendar day from
 * disbursement to settlement (or to now, for a still-outstanding loan). Not
 * compounding. Mirrors backend calculateAccruedInterestVnd, which is what a
 * repayment actually charges (gốc + lãi, frozen on the PaymentIntent) — so the
 * client can show the same "Tổng cần trả" before an intent exists.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DAYS_PER_YEAR = 365;

function daysBetween(from: string, to: string): number {
  const fromMs = new Date(from).getTime();
  const toMs = new Date(to).getTime();
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) return 0;
  return Math.floor((toMs - fromMs) / MS_PER_DAY);
}

export interface AccruedInterest {
  days: number;
  ratePercentPerYear: number;
  accruedVnd: number;
  totalOwedVnd: number;
}

export function calculateAccruedInterest(principalVnd: number, ratePercentPerYear: number, disbursedAt: string, asOf: string): AccruedInterest {
  const days = daysBetween(disbursedAt, asOf);
  const accruedVnd = Math.round(principalVnd * (ratePercentPerYear / 100) * (days / DAYS_PER_YEAR));
  return { days, ratePercentPerYear, accruedVnd, totalOwedVnd: principalVnd + accruedVnd };
}

/** FARMER-scoped override > HTX-scoped override > the loan's own stored rate
 * — mirrors InterestRateService.resolveEffectiveRate on the backend exactly. */
export function resolveEffectiveRatePercent(
  fallbackRatePercentPerYear: number,
  farmerOverrideRate?: number,
  htxOverrideRate?: number,
): number {
  if (farmerOverrideRate !== undefined) return farmerOverrideRate;
  if (htxOverrideRate !== undefined) return htxOverrideRate;
  return fallbackRatePercentPerYear;
}

/** Gốc + lãi owed right now (or as of settlement). Uses the full current
 * timestamp like the backend does — a date-only "as of" counted a day early or
 * late against what the repayment then charged. */
export function estimateAmountOwed(principalVnd: number, ratePercentPerYear: number, disbursedAt: string, settledAt?: string): AccruedInterest {
  return calculateAccruedInterest(principalVnd, ratePercentPerYear, disbursedAt, settledAt || new Date().toISOString());
}
